import { equal, isNegative, isPositive, isZero, subtract } from "dinero.js";
import { Immutable } from "immer";
import { t } from "ttag";

import { IProduct, InternalProduct } from "../../../models/catalogue";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { format } from "../../../utils/format";
import { ZERO, invert } from "../../../utils/money";

export const buildUpsellProductName = (product: InternalProduct): string => {
    if (product.parent) {
        if (product.parent.attributes.upgrade_option_title) {
            return product.parent.attributes.upgrade_option_title.value;
        }
        return product.parent.title;
    }

    if (product.attributes.upgrade_option_title) {
        return product.attributes.upgrade_option_title.value;
    }

    return product.title;
};

export const buildUpsellPrice = (
    product: InternalProduct,
    baseVariant: IProduct | null,
    options: Immutable<{
        isFreeGift: boolean;
        isRelative: boolean;
        isOneProduct: boolean;
    }>,
): { price: string; strikethroughPrice: string | null } => {
    if (options.isFreeGift) {
        const price = getProductPrice(product.price, {
            priceType: PriceType.RETAIL,
            includePostDiscountAddons: true,
            quantity: 1,
        });
        const formattedPrice = format.money(price);
        return {
            price: t`(${formattedPrice} Value)`,
            strikethroughPrice: null,
        };
    }

    let baseCosmetic = ZERO;
    let baseRetail = ZERO;
    if (baseVariant && options.isRelative) {
        baseCosmetic = getProductPrice(baseVariant.price, {
            priceType: PriceType.COSMETIC_EXCL_TAX,
            includePostDiscountAddons: true,
            quantity: 1,
        });
        baseRetail = getProductPrice(baseVariant.price, {
            priceType: PriceType.RETAIL,
            includePostDiscountAddons: true,
            quantity: 1,
        });
    }

    const upgradeCosmetic = getProductPrice(product.price, {
        priceType: PriceType.COSMETIC_EXCL_TAX,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    const upgradeRetail = getProductPrice(product.price, {
        priceType: PriceType.RETAIL,
        includePostDiscountAddons: true,
        quantity: 1,
    });

    const cosmeticDiff = subtract(upgradeCosmetic, baseCosmetic);
    const retailDiff = subtract(upgradeRetail, baseRetail);

    if (options.isOneProduct) {
        let price: string;
        if (isZero(cosmeticDiff) || equal(cosmeticDiff, invert(baseCosmetic))) {
            price = "";
        } else {
            const formattedDiff = format.money(cosmeticDiff);
            price = t`+ ${formattedDiff}`;
        }
        return {
            price,
            strikethroughPrice:
                price !== "" &&
                !equal(retailDiff, cosmeticDiff) &&
                isPositive(retailDiff) &&
                !isNegative(subtract(retailDiff, cosmeticDiff))
                    ? format.money(retailDiff)
                    : null,
        };
    }

    // Multi-product (modal multi-column) path
    let price: string;
    let strikethroughPrice: string | null = null;

    if (isZero(cosmeticDiff) || equal(cosmeticDiff, invert(baseCosmetic))) {
        price = "";
    } else if (isNegative(cosmeticDiff)) {
        price = format.money(invert(cosmeticDiff));
    } else {
        const formattedDiff = format.money(cosmeticDiff);
        price = t`+ ${formattedDiff}`;
        if (
            !equal(retailDiff, cosmeticDiff) &&
            isPositive(retailDiff) &&
            !isNegative(subtract(retailDiff, cosmeticDiff))
        ) {
            strikethroughPrice = format.money(retailDiff);
        }
    }

    return { price, strikethroughPrice };
};
